import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatAccordion } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { ChangePassword, CustomerConnection, CustomerType } from '@app/customers-base';
import { OrderService, PhoneBookEntry, PhoneBookEntryService } from '@app/orders-base';
import { GenericFormComponent, Salutation } from '@messaia/cdk';
import { Profile } from '../../models/profile';
import { ProfileService } from '../../services/profile.service';

@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.scss']
})
export class ProfileComponent extends GenericFormComponent<Profile, ProfileService> {
	/**
	 * Form title
	 */
	public subtitle: string = $localize`:@@profile:Profile`

	/**
	 * Edit mode status
	 */
	public isEditMode: boolean = true;

	/**
	 * Salutation type
	 */
	public Salutation: typeof Salutation = Salutation;

	/**
	 * Customer type
	 */
	public CustomerType: typeof CustomerType = CustomerType;

	/**
	 * Change password object
	 */
	public newPassword: ChangePassword = new ChangePassword();

	/**
	 * Toggle password visibility
	 */
	public hidePassword: boolean = true;

	/**
	 * phoneBookEntries: PhoneBookEntry[]
	 */
	public phoneBookEntries: PhoneBookEntry[] = [];

	/**
	 * Accordion ref
	 */
	@ViewChild(MatAccordion)
	public accordion: MatAccordion;

	/**
	 * Constructor
	 */
	constructor(
		protected service: ProfileService,
		protected orderService: OrderService,
		protected phoneBookEntryService: PhoneBookEntryService,
		protected route: ActivatedRoute,
		protected changeDetectorRef: ChangeDetectorRef
	) {
		super(Profile, service, route, changeDetectorRef)
		this.isLoading = true;
	}

	/**
	 * Loads an item by ID
	 */
	public loadItem(): void {
		this.service.get('current', { includes: ['Connections'] }, { 'Disable-Cache': 'Yes' })
			.subscribe(item => {
				this.item = Object.assign(this.newInstance(), <Profile>item);
				this.onAfterItemLoaded();
				this.isLoading = false;
			}, e => this.isLoading = false);
	}

	/**
	 * updates the entity
	 */
	public patch(fields: Array<string>, operation: string = 'replace'): void {
		this.isSaving = true;
		console.log(operation);

		/* Build patch methods */
		let patches = fields.map(x => { return { op: operation, path: `/${x}`, value: this.item[x] }; });

		/* Send request */
		this.service
			.patch(this.item.id, patches, null, { headers: { 'Show-Message': 'Yes' } })
			.subscribe(x => {
				this.isSaving = false;
				this.accordion.multi = true;
				this.accordion.closeAll();
				this.accordion.multi = false;
			}, _ => this.isSaving = false);
	}

	/**
	 * updates the entity
	 */
	public patchLineOption(customerConnection: CustomerConnection): void {
		this.isSaving = true;

		/* Send request */
		this.service
			.updateLineOption(this.item.id, customerConnection.id, customerConnection.lineOption)
			.subscribe(x => {
				this.isSaving = false;
				this.accordion.multi = true;
				this.accordion.closeAll();
				this.accordion.multi = false;
			}, _ => this.isSaving = false);
	}

	/**
	 * Changes password
	 */
	public changePassword(): void {
		this.newPassword.id = this.item.id;
		this.service.changePassword(this.newPassword).subscribe(() => {
			this.snackBarService.open('Ihr Passwort wurde erfolgreich geändert', "OK", { duration: 3000, panelClass: ['primary'] })
		}, error => this.parseErrors(error));
	}

	/**
	 * Loads phone book entries of the current customer
	 */
	public loadPhoneBookEntries(): void {
		this.phoneBookEntryService.getList({ 'order.customerId': this.item.id }).subscribe(x => this.phoneBookEntries = x);
	}

	/**
	 * Adds a phone book entry
	 */
	public addPhoneBookEntry(): void {
		this.orderService.getList({ archived: false, cancelled: false, terminated: false }).subscribe(x => {
			if (x && x[0]) {
				this.phoneBookEntries.push(Object.assign(new PhoneBookEntry(), { orderId: x[0].id }));
			} else {
				this.phoneBookEntries.push(Object.assign(new PhoneBookEntry(), {}));
			}
		});
	}

	/**
	 * Removes a phone book entry
	 * @param phoneBookEntry 
	 */
	public removePhoneBookEntry(phoneBookEntry: PhoneBookEntry): void {
		if (phoneBookEntry) {
			if (!phoneBookEntry.id) {
				this.phoneBookEntries.remove(phoneBookEntry);
			} else {
				this.showDeleteItemDialog().subscribe((accept: boolean) => {
					if (accept) {
						this.phoneBookEntryService.delete(phoneBookEntry.id).subscribe(x => {
							this.showMessage(`Datensatz erfolgreich gelöscht.`);
							this.isSaving = false;
							this.loadPhoneBookEntries();
						}, () => this.isSaving = false);
					}
				});
			}
		}
	}

	/**
	 * Saves phone book entry
	 * @param phoneBookEntry 
	 */
	public savePhoneBookEntry(phoneBookEntry: PhoneBookEntry, form: NgForm): void {
		if (form && !this.isFormValid(form)) {
			return;
		}

		this.isSaving = true;
		if (phoneBookEntry.id) {
			this.phoneBookEntryService
				.update(phoneBookEntry.id, phoneBookEntry, null, Object.assign({}, { headers: this.toHttpHeaders({ 'Show-Message': 'yes' }) }), false)
				.subscribe(x => {
					this.loadPhoneBookEntries();
					this.isSaving = false;
				}, _ => this.isSaving = false);
		} else {
			this.phoneBookEntryService
				.create<PhoneBookEntry>(phoneBookEntry, null, Object.assign({}, { headers: this.toHttpHeaders({ 'Show-Message': 'yes' }) }), false)
				.subscribe(x => {
					this.loadPhoneBookEntries();
					this.isSaving = false;
				}, _ => this.isSaving = false);
		}
	}
}